import { forwardRef, useState } from "react";

import { DndContext, useSensor, useSensors } from "@dnd-kit/core";
import classNames from "classnames";
import PropTypes from "prop-types";

import "./boardview.scss";
import { closestCenter } from "./closestCenter";
import { DRAGGABLE_TYPES, MEASURING_CONFIG } from "./constants";
import DndOverlay from "./Overlay";
import Section from "./Section";
import SectionContainer from "./Section/Container";
import SectionPlaceholder from "./Section/Placeholder";
import { getPlatformSensor } from "./utils";

const [sensor, sensorConfig] = getPlatformSensor();

const BoardView = forwardRef((props, ref) => {
  const {
    isDragAndDropDisabled = false,
    sections,
    onMoveSection,
    onMoveItem,
    renderSection,
    className,
  } = props;

  const [dragState, setDragState] = useState({});
  const sensors = useSensors(useSensor(sensor, sensorConfig));

  const onDragEnd = drag => {
    if (isDragAndDropDisabled) return;

    const active = dragState.active.data.current;
    const over = drag.over?.data.current;
    if (!active || !over) return;

    if (active.type === DRAGGABLE_TYPES.ITEM) {
      onMoveItem?.(active, over);
    } else if (active.type === DRAGGABLE_TYPES.SECTION) {
      onMoveSection?.(active, over);
    }
  };

  return (
    <DndContext
      {...{ onDragEnd }}
      collisionDetection={closestCenter(dragState)}
      measuring={MEASURING_CONFIG}
      sensors={isDragAndDropDisabled ? [] : sensors}
      onDragStart={isDragAndDropDisabled ? undefined : setDragState}
    >
      <div
        {...{ ref }}
        className={classNames(
          "neeto-molecules-boardview-outer-wrapper flex w-full flex-1 overflow-x-auto",
          className
        )}
      >
        <SectionPlaceholder />
        {sections.map((section, index) => (
          <SectionContainer
            {...{ index, isDragAndDropDisabled }}
            data={{ section }}
            id={section.id}
            key={section.id}
            render={() =>
              renderSection({ index, section, isDragAndDropDisabled })
            }
          />
        ))}
      </div>
      <DndOverlay {...{ props }} state={dragState} />
    </DndContext>
  );
});

BoardView.displayName = "BoardView";
BoardView.Section = Section;
BoardView.propTypes = {
  /** A reference to the container element. */
  // eslint-disable-next-line react/no-unused-prop-types
  ref: PropTypes.any,

  /**
   An array of sections, each defined by an object with an `id` string.
   */
  sections: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired })
  ).isRequired,

  /** function to render each section content in the board view  */
  renderSection: PropTypes.func.isRequired,

  /** A function to render a drag overlay for individual sections (drag preview) */
  // eslint-disable-next-line react/no-unused-prop-types
  renderSectionOverlay: PropTypes.func.isRequired,

  /** A function to render a drag overlay for individual items (drag preview) */
  // eslint-disable-next-line react/no-unused-prop-types
  renderItemOverlay: PropTypes.func.isRequired,

  /** Callback function triggered when a section is reordered */
  onMoveSection: PropTypes.func,

  /** Callback function triggered when an item is moved */
  onMoveItem: PropTypes.func,

  /** When true, disables all drag and drop functionality for the board view */
  isDragAndDropDisabled: PropTypes.bool,

  /**
   * Additional classes passed on to the board view.
   */
  className: PropTypes.string,
};

export default BoardView;
